import {
  CompensationReview,
  CompensationReviewItem,
  PayBand,
  PayFrequency,
  ReviewResults,
  ReviewStatus,
} from './types';

// Factors that normalise a pay amount of a given frequency to a monthly figure.
// Mirrors the Salesforce Results panel: Monthly ×1, Annual ÷12,
// Weekly ×52/12, Bi-weekly ×26/12, Hourly ×173.33.
const MONTHLY_FACTOR: Record<PayFrequency, number> = {
  MONTHLY: 1,
  ANNUAL: 1 / 12,
  WEEKLY: 52 / 12,
  BIWEEKLY: 26 / 12,
  HOURLY: 173.33,
};

export const toMonthly = (amount: number, frequency: PayFrequency): number =>
  amount * MONTHLY_FACTOR[frequency];

const round2 = (value: number): number => Math.round(value * 100) / 100;

// New amount derived from a merit percentage applied to the reference amount.
export const applyMeritPercent = (
  referenceAmount: number,
  meritPercent: number,
): number => round2(referenceAmount * (1 + meritPercent / 100));

// The merit percentage implied by a reference → new amount change.
export const meritFromAmounts = (
  referenceAmount: number,
  newAmount: number,
): number => {
  if (referenceAmount === 0) {
    return 0;
  }
  return round2(((newAmount - referenceAmount) / referenceAmount) * 100);
};

// Totals across the *included* rows only, normalised to monthly equivalents.
export const computeResults = (
  items: CompensationReviewItem[],
): ReviewResults => {
  const included = items.filter((item) => item.included);

  const totalReferenceMonthly = included.reduce(
    (sum, item) => sum + toMonthly(item.referenceAmount, item.frequency),
    0,
  );
  const totalNewMonthly = included.reduce(
    (sum, item) => sum + toMonthly(item.newAmount, item.frequency),
    0,
  );

  const netChangeAbsolute = totalNewMonthly - totalReferenceMonthly;
  const netChangePercent =
    totalReferenceMonthly === 0
      ? 0
      : (netChangeAbsolute / totalReferenceMonthly) * 100;

  return {
    totalReferenceMonthly: round2(totalReferenceMonthly),
    totalNewMonthly: round2(totalNewMonthly),
    netChangeAbsolute: round2(netChangeAbsolute),
    netChangePercent: round2(netChangePercent),
  };
};

// A proposed amount is out-of-band when it falls outside the linked pay band.
export const isOutOfBand = (
  newAmount: number,
  payBand: PayBand | null,
): boolean => {
  if (!payBand) {
    return false;
  }
  return newAmount < payBand.min || newAmount > payBand.max;
};

// Where a value sits inside its band, as a 0–100 percentage (clamped). Used to
// position the marker on the pay-band bar.
export const bandPenetration = (amount: number, payBand: PayBand): number => {
  if (payBand.max <= payBand.min) {
    return 100;
  }
  const ratio = (amount - payBand.min) / (payBand.max - payBand.min);
  return Math.min(100, Math.max(0, ratio * 100));
};

type StatusVariant = 'secondary' | 'info' | 'success' | 'danger' | 'warning';

// Maps a review status to a Bootstrap badge variant + translation key suffix.
export const statusMeta = (
  status: ReviewStatus,
): { variant: StatusVariant; labelKey: string } => {
  switch (status) {
    case 'PREPARING':
      return { variant: 'secondary', labelKey: 'preparing' };
    case 'SUBMITTED':
      return { variant: 'info', labelKey: 'submitted' };
    case 'APPROVED':
      return { variant: 'success', labelKey: 'approved' };
    case 'EXECUTED':
      return { variant: 'success', labelKey: 'executed' };
    case 'REJECTED':
      return { variant: 'danger', labelKey: 'rejected' };
    case 'FAILED':
      return { variant: 'danger', labelKey: 'failed' };
    case 'REVERTED':
      return { variant: 'warning', labelKey: 'reverted' };
    case 'UNKNOWN':
      return { variant: 'secondary', labelKey: 'unknown' };
  }
};

// A reviewer may edit/submit only while the review is still preparing.
export const canEdit = (review: CompensationReview): boolean =>
  review.myRole === 'REVIEWER' && review.status === 'PREPARING';

// A submitted review can be decided whenever there is an approval request
// routed to the running user (`approvalRequestId`). This is keyed off the
// request rather than `myRole` so that an assessing manager who is *also* the
// routed approver isn't locked out (myRole resolves to REVIEWER for them);
// a reviewer with no routed request has `approvalRequestId === null`, so they
// still don't get the action.
export const canApproveOrReject = (review: CompensationReview): boolean =>
  review.status === 'SUBMITTED' && review.approvalRequestId !== null;

// Reviews that need an action from the running user — drives the sidebar badge.
export const needsMyAction = (review: CompensationReview): boolean =>
  canEdit(review) || canApproveOrReject(review);

// Revert and audit logs stay in Salesforce; the Hub surfaces a pointer only.
export const isManagedInSalesforceOnly = (
  review: CompensationReview,
): boolean =>
  review.status === 'EXECUTED' ||
  review.status === 'REVERTED' ||
  review.status === 'FAILED';
